import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import {
  useGetReviewTicketReportTemplateQuery,
  useAddReviewTicketReportMutation,
} from '../../api/reviewTicketAPI';
import { standardJsonResponseSync } from '../../modules/fetch';
import { logMessage } from '../../logger';
import { setValuesFormTemplate } from '../../api/setValuesFormTemplate';
import Error from '../../components/Error';
import ReviewTicketFormTemplateSteps from '../../components/ReviewTicketFormTemplateSteps';
import { Center, Container } from './styles';

type SmallChatItem = {
  _id: { $oid: string };
  entity: 'customer' | 'order' | string;
  name: string | '';
  assigneeName: string | '';
  store?: { _id: { $oid: string } };
};

export const ReviewTicket = ({
  onClose,
  isPreview,
  setIsPreview,
  isPreviewTicket,
  content,
  review,
  customer,
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [inputValues, setInputValues] = useState({});
  const [requiredFields, setRequiredFields] = useState({});
  const [inputLabels, setInputLabels] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('unknown.error');
  const {
    data: templateReport,
    error: errorTemplate,
    isError: isErrorTemplate,
  } = useGetReviewTicketReportTemplateQuery(review?._id.$oid, { skip: !review?._id.$oid });
  const [addReviewTicketReport] = useAddReviewTicketReportMutation();

  useEffect(() => {
    if (isErrorTemplate) {
      logMessage('error', 'Template fetch failed', errorTemplate);
      const response = { ...errorTemplate } as { data?: unknown; status?: number };
      try {
        standardJsonResponseSync(response);
        setError('unknown.error');
      } catch (ResponseError) {
        setError((ResponseError as Error).message);
      }
    }
  }, [isErrorTemplate, errorTemplate]);

  useEffect(() => {
    if (content && Object.keys(content).length !== 0) {
      setInputValues(content);
      setInputLabels(content);
    } else {
      const temp = setValuesFormTemplate(templateReport?.data ?? {});
      setInputValues(temp.inputValues);
      setRequiredFields(temp.requiredFields);
      setInputLabels(temp.inputValues);
    }
  }, [templateReport, content]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const report = Object.assign({}, inputValues, inputLabels);
    await addReviewTicketReport({
      id: review?._id.$oid,
      ticket: {
        report: report,
      },
    }).then((response) => {
      setIsSubmitting(false);
      if (!response) return;

      if ('error' in response) {
        const errorObj: any = response.error;
        const status = errorObj?.status;
        if (typeof status === 'number' && status !== 200) {
          const errors = errorObj?.data?.errors;
          if (errors) {
            const message = errors
              .map((item) => {
                const regex = /(\w+)\]$/g;
                const exec = regex.exec(item.path);
                const field = exec?.[1];
                if (field) {
                  let fieldName = String(field).replace(
                    /([-_]\w)/g,
                    (g) => ' ' + g[1].toUpperCase(),
                  );
                  fieldName = fieldName.slice(0, 1).toUpperCase() + fieldName.slice(1);
                  return `${fieldName}: ${item.message}`;
                }
                return item.message;
              })
              .filter(Boolean);
            alert(`Error\n${message.join('\n')}`);
          } else {
            alert('Internal Server Error');
          }
          return;
        }
      }

      if ('data' in response) {
        const payload: any = response.data;
        const statusCode = payload?.meta?.statusCode;
        if (statusCode === 200) {
          if (customer?.id) {
            handleChatCustomer();
          }
          const createdTicketId =
            payload?.data?.id ??
            payload?.data?._id?.$oid ??
            payload?.data?._id ??
            payload?.data?.ticketId ??
            undefined;
          onClose(createdTicketId);
        } else {
          alert('Unexpected status. Internal Server Error');
        }
      }
    });
  };

  const handleChatCustomer = () => {
    const chatItem: SmallChatItem = {
      _id: { $oid: customer?.id ?? '' },
      entity: 'customer',
      name: customer?.name ?? '',
      assigneeName: customer?.assigneeName ?? '',
      store: { _id: { $oid: review?.storeId ?? '' } },
    };
    navigate('/messages', { state: { smallChatTarget: chatItem } });
  };

  return (
    <Container>
      {!templateReport?.data && (
        <>
          <Center>
            <FormattedMessage id={'modal.loading'} defaultMessage={'Loading...'} />
          </Center>
        </>
      )}
      {(isErrorTemplate && <Error message={error ?? 'unknown.error'} />) ||
        (templateReport?.data?.length ? (
          <ReviewTicketFormTemplateSteps
            inputValues={inputValues}
            requiredFields={requiredFields}
            setInputValues={setInputValues}
            inputLabels={inputLabels}
            setInputLabels={setInputLabels}
            handleFormSubmit={handleFormSubmit}
            reportFormData={templateReport?.data}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            isPreview={isPreview}
            setIsPreview={setIsPreview}
            isPreviewTicket={isPreviewTicket}
            handleChatCustomer={handleChatCustomer}
            customer={customer}
          />
        ) : null)}
    </Container>
  );
};
export default ReviewTicket;
